import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Chip, Alert,
  Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';

const isoDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  return new Date(d).toISOString().slice(0, 10);
};

const formatRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusOptions = ['All', 'Paid', 'Pending', 'Processing'];

const commissionColumns = [
  { field: 'caseRef', headerName: 'Case ID', width: 130 },
  { field: 'applicant', headerName: 'Applicant', width: 190 },
  { field: 'product', headerName: 'Product', width: 130 },
  { field: 'bank', headerName: 'Bank', width: 140 },
  {
    field: 'loanAmount', headerName: 'Loan Amount', width: 140, align: 'right',
    renderCell: (row) => row.loanAmount > 0 ? `RM ${Number(row.loanAmount).toLocaleString('en-MY')}` : '-',
  },
  {
    field: 'rate', headerName: 'Rate', width: 80, align: 'center',
    renderCell: (row) => row.rate || '-',
  },
  {
    field: 'commissionAmount', headerName: 'Commission (RM)', width: 160, align: 'right',
    renderCell: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#00B894' }}>
        {formatRM(row.commissionAmount)}
      </Typography>
    ),
  },
  {
    field: 'status', headerName: 'Status', width: 140,
    renderCell: (row) => <StatusBadge status={row.status} />,
  },
  {
    field: 'payoutDate', headerName: 'Payout Date', width: 130,
    renderCell: (row) => row.payoutDate ? isoDate(row.payoutDate) : '-',
  },
];

export default function CommissionStatement() {
  const { user } = useAuth();
  const [refreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 89);
    return { start: isoDate(start), end: isoDate(end) };
  });

  // Source: own referral cases with positive commission
  const baseRecords = useMemo(() => {
    if (!user) return [];
    return db.cases
      .getForPartner(user.id, false)
      .filter((c) => Number(c.commissionAmount) > 0)
      .map((c, idx) => ({
        id: c.id || idx,
        caseRef: c.id,
        applicant: c.applicantName,
        product: c.productName,
        bank: c.bank,
        loanAmount: Number(c.amount) || 0,
        rate: '(computed)',
        commissionAmount: Number(c.commissionAmount) || 0,
        status: c.commissionStatus === 'paid' ? 'Paid' : 'Pending',
        payoutDate: c.status === 'Disbursed' ? c.updatedAt : null,
        _createdAt: c.createdAt,
      }));
    // eslint-disable-next-line
  }, [user, refreshKey]);

  // Apply filters (date + status)
  const filteredRecords = useMemo(() => {
    const startMs = new Date(dateRange.start).getTime();
    const endMs = new Date(dateRange.end + 'T23:59:59').getTime();
    return baseRecords.filter((r) => {
      const t = new Date(r._createdAt).getTime();
      if (!isNaN(t) && (t < startMs || t > endMs)) return false;
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      return true;
    });
  }, [baseRecords, dateRange, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let totalEarned = 0;
    let pendingPayout = 0;
    let paidThisMonth = 0;
    let ytd = 0;

    baseRecords.forEach((r) => {
      const amt = Number(r.commissionAmount) || 0;
      const d = new Date(r._createdAt);
      if (r.status === 'Paid') {
        totalEarned += amt;
        if (d.getFullYear() === currentYear) ytd += amt;
        if (d.getFullYear() === currentYear && d.getMonth() === currentMonth) {
          paidThisMonth += amt;
        }
      } else {
        pendingPayout += amt;
      }
    });

    return { totalEarned, pendingPayout, paidThisMonth, ytd };
  }, [baseRecords]);

  const totalFiltered = filteredRecords.reduce((sum, r) => sum + (Number(r.commissionAmount) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            Commission Statement
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your commission earnings and payout history
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          sx={{
            borderColor: PRIMARY, color: PRIMARY, borderRadius: 2,
            textTransform: 'none', fontWeight: 600,
            '&:hover': { borderColor: '#142A4D', backgroundColor: '#F0F4F8' },
          }}
        >
          Export Statement
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {statusOptions.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                onClick={() => setStatusFilter(s)}
                sx={{
                  fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer',
                  backgroundColor: statusFilter === s ? PRIMARY : '#E0E0E0',
                  color: statusFilter === s ? 'white' : '#333',
                  '&:hover': { backgroundColor: statusFilter === s ? '#142847' : '#BDBDBD' },
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Earned"
            value={formatRM(stats.totalEarned)}
            icon={AccountBalanceWalletIcon}
            color="#1B365D"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Payout"
            value={formatRM(stats.pendingPayout)}
            icon={HourglassEmptyIcon}
            subtitle="Expected next cycle"
            color="#F39C12"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Paid This Month"
            value={formatRM(stats.paidThisMonth)}
            icon={PaidIcon}
            color="#00B894"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="YTD Earnings"
            value={formatRM(stats.ytd)}
            icon={TrendingUpIcon}
            color="#6C5CE7"
          />
        </Grid>
      </Grid>

      {/* Commission Table */}
      {filteredRecords.length > 0 ? (
        <>
          <DataTable
            title="Commission Records"
            columns={commissionColumns}
            rows={filteredRecords}
            pageSize={10}
            exportable
          />

          {/* Footer Total Row */}
          <Paper sx={{ mt: -1, borderRadius: '0 0 12px 12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Table>
              <TableBody>
                <TableRow sx={{ backgroundColor: PRIMARY }}>
                  <TableCell colSpan={6} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                    Total ({filteredRecords.length} records)
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#00B894', fontWeight: 700, fontSize: '1rem', py: 2 }}>
                    {formatRM(totalFiltered)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>No data yet</Alert>
      )}
    </Box>
  );
}
